/* Static mock of AISummaryBanner for the demo app.
   Supports style switching (Concise/Regular/Comprehensive) with different summaries,
   and hoverable citation highlights that show linked mock items. */

import { useState, useRef, useEffect } from "react";
import { createPortal } from "react-dom";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sparkles,
  RefreshCw,
  ChevronDown,
  ChevronUp,
  Clock,
  Plus,
} from "lucide-react";

const HIGHLIGHT_CLASS = "ring-2 ring-primary/60 rounded-md transition-all duration-300";

const STYLE_KEYS = ["concise", "regular", "comprehensive"] as const;
const STYLE_LABELS = ["Concise", "Regular", "Comprehensive"] as const;
type StyleKey = (typeof STYLE_KEYS)[number];

const MOCK_DATE = (() => {
  const d = new Date();
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
})();

/* ─── Mock citation items (referenced by highlight segments) ─── */

export interface MockCitation {
  id: string;
  title: string;
  source: string;
  type: string;
  duration: string;
}

const CITATIONS: Record<string, MockCitation[]> = {
  "gpt5": [
    { id: "digest-gpt5-0", title: "GPT-5: What's New and Why It Matters", source: "The Verge", type: "article", duration: "12 min" },
    { id: "digest-gpt5-1", title: "GPT-5 Full Breakdown — First Impressions", source: "Fireship", type: "video", duration: "9 min" },
  ],
  "deepmind": [
    { id: "digest-deepmind-0", title: "DeepMind's Video Diffusion Explained", source: "Two Minute Papers", type: "video", duration: "18 min" },
  ],
  "react20": [
    { id: "digest-react20-0", title: "React 20 Signals Deep Dive", source: "Fireship", type: "video", duration: "14 min" },
    { id: "digest-react20-1", title: "Signals in React: The Full Story", source: "Syntax.fm", type: "podcast", duration: "42 min" },
  ],
  "visionos": [
    { id: "digest-visionos-0", title: "Apple Updated HIG for visionOS 3", source: "Apple Developer", type: "article", duration: "8 min" },
  ],
  "vercel": [
    { id: "digest-vercel-0", title: "Vercel Edge Middleware v2 — 60% Faster", source: "Vercel Blog", type: "article", duration: "5 min" },
  ],
  "figma": [
    { id: "digest-figma-0", title: "Figma's AI Layout Feature Demo", source: "Figma", type: "video", duration: "6 min" },
  ],
  "docker": [
    { id: "digest-docker-0", title: "Docker Compose v3 GPU Orchestration", source: "Docker Blog", type: "article", duration: "7 min" },
  ],
  "tailwind": [
    { id: "digest-tailwind-0", title: "Tailwind CSS 5.0 — What Changed", source: "Tailwind Labs", type: "video", duration: "11 min" },
  ],
  "bun": [
    { id: "digest-bun-0", title: "Bun 2.0 Runtime Performance Deep Dive", source: "Jarred Sumner", type: "video", duration: "15 min" },
  ],
};

/* ─── Per-style summary points ─── */

interface Segment {
  text: string;
  citationKey?: string; // key into CITATIONS
}

interface SummaryPoint {
  topic: string;
  segments: Segment[];
}

const SUMMARIES: Record<StyleKey, SummaryPoint[]> = {
  concise: [
    {
      topic: "AI & ML",
      segments: [
        { text: "GPT-5 released", citationKey: "gpt5" },
        { text: " with 40% math gains. " },
        { text: "DeepMind video diffusion paper", citationKey: "deepmind" },
        { text: " published." },
      ],
    },
    {
      topic: "Web Dev",
      segments: [
        { text: "React 20 RC adds signals", citationKey: "react20" },
        { text: "." },
      ],
    },
    {
      topic: "Design",
      segments: [
        { text: "visionOS 3 HIG updated", citationKey: "visionos" },
        { text: "." },
      ],
    },
  ],
  regular: [
    {
      topic: "AI & ML",
      segments: [
        { text: "OpenAI released GPT-5 with improved reasoning", citationKey: "gpt5" },
        { text: " — early benchmarks show 40% gains on math tasks. Meanwhile, " },
        { text: "Google DeepMind published new diffusion model research", citationKey: "deepmind" },
        { text: " focused on video generation quality." },
      ],
    },
    {
      topic: "Web Dev",
      segments: [
        { text: "React 20 RC introduces built-in signals", citationKey: "react20" },
        { text: " for fine-grained reactivity. Several popular libraries already ship adapters." },
      ],
    },
    {
      topic: "Design",
      segments: [
        { text: "Figma announced AI-powered layout suggestions", citationKey: "figma" },
        { text: " and " },
        { text: "Apple updated HIG for visionOS 3", citationKey: "visionos" },
        { text: " with new spatial interaction patterns." },
      ],
    },
    {
      topic: "DevOps",
      segments: [
        { text: "Docker released Compose v3 with native GPU orchestration", citationKey: "docker" },
        { text: ". " },
        { text: "Vercel shipped edge middleware v2", citationKey: "vercel" },
        { text: " reducing cold start latency by 60%." },
      ],
    },
  ],
  comprehensive: [
    {
      topic: "AI & ML",
      segments: [
        { text: "OpenAI released GPT-5 with improved reasoning", citationKey: "gpt5" },
        { text: " — early benchmarks show 40% gains on math tasks and notable improvements in long-context retrieval. Meanwhile, " },
        { text: "Google DeepMind published new diffusion model research", citationKey: "deepmind" },
        { text: " focused on video generation quality, introducing a novel temporal consistency mechanism that outperforms prior approaches on VBench." },
      ],
    },
    {
      topic: "Web Dev",
      segments: [
        { text: "React 20 RC introduces built-in signals", citationKey: "react20" },
        { text: " for fine-grained reactivity, drawing from Preact and Solid.js patterns. Several popular libraries already ship adapters. Additionally, " },
        { text: "Tailwind CSS 5.0 launched", citationKey: "tailwind" },
        { text: " with native cascade layers and a rewritten JIT engine that cuts build times in half." },
      ],
    },
    {
      topic: "Design",
      segments: [
        { text: "Figma announced AI-powered layout suggestions", citationKey: "figma" },
        { text: " that auto-arrange frames based on content hierarchy. " },
        { text: "Apple updated HIG for visionOS 3", citationKey: "visionos" },
        { text: " with new spatial interaction patterns including gaze-and-pinch combos for complex multi-step workflows." },
      ],
    },
    {
      topic: "DevOps",
      segments: [
        { text: "Docker released Compose v3 with native GPU orchestration", citationKey: "docker" },
        { text: ", simplifying ML workload deployment. " },
        { text: "Vercel shipped edge middleware v2", citationKey: "vercel" },
        { text: " reducing cold start latency by 60% through a new V8 isolate pooling strategy." },
      ],
    },
    {
      topic: "Tooling",
      segments: [
        { text: "Bun 2.0 hit stable", citationKey: "bun" },
        { text: " with full Node.js compatibility, native S3 client, and a macro system for compile-time code generation. Early adopters report 3× faster CI runs compared to Node + npm." },
      ],
    },
  ],
};


/* ─── Main component ─── */

interface DemoDailyDigestProps {
  highlightedFeature?: string;
  onAddItem?: (citation: MockCitation) => void;
  queuedIds?: Set<string>;
}

export function DemoDailyDigest({ highlightedFeature, onAddItem, queuedIds }: DemoDailyDigestProps) {
  const [isCollapsed, setIsCollapsed] = useState(false);
  const [activeStyle, setActiveStyle] = useState<StyleKey>("regular");

  const isHighlighted = highlightedFeature === "daily-digest";

  return (
    <div className={`mx-4 md:mx-6 mb-2 rounded-lg border border-border bg-card overflow-hidden ${isHighlighted ? HIGHLIGHT_CLASS : ""}`}>
      <DigestHeader
        isCollapsed={isCollapsed}
        activeStyle={activeStyle}
        onToggle={() => setIsCollapsed((v) => !v)}
        onSetStyle={setActiveStyle}
      />
      {!isCollapsed && <DigestBody activeStyle={activeStyle} onAddItem={onAddItem} queuedIds={queuedIds} />}
    </div>
  );
}

/* ─── Header ─── */

function DigestHeader({
  isCollapsed,
  activeStyle,
  onToggle,
  onSetStyle,
}: {
  isCollapsed: boolean;
  activeStyle: StyleKey;
  onToggle: () => void;
  onSetStyle: (s: StyleKey) => void;
}) {
  return (
    <div
      className="flex items-center gap-2 px-3 py-2 cursor-pointer select-none transition-colors hover:bg-accent/50"
      onClick={onToggle}
    >
      <Sparkles className="size-4 shrink-0 text-primary" />
      <span className="text-sm font-semibold">Daily Digest</span>
      <span className="text-xs text-muted-foreground hidden sm:inline">{MOCK_DATE}</span>

      {isCollapsed && (
        <span className="text-xs text-muted-foreground ml-auto mr-1 hidden sm:inline">
          28 items &middot; ~1h 45m
        </span>
      )}

      {!isCollapsed && (
        <div className="ml-auto flex items-center gap-1" onClick={(e) => e.stopPropagation()}>
          <div className="hidden sm:flex items-center gap-0.5 mr-1">
            {STYLE_KEYS.map((key, i) => (
              <Button
                key={key}
                variant={activeStyle === key ? "outline" : "ghost"}
                size="sm"
                className="h-6 px-2 text-[11px]"
                onClick={() => onSetStyle(key)}
              >
                {STYLE_LABELS[i]}
              </Button>
            ))}
          </div>
          <Badge variant="outline" className="text-[11px] px-1.5 py-0 h-5 hidden sm:inline-flex">
            28 items
          </Badge>
          <Button variant="ghost" size="icon" className="size-7">
            <RefreshCw className="size-3.5" />
          </Button>
        </div>
      )}

      <Button
        variant="ghost"
        size="icon"
        className={`size-7 shrink-0 ${isCollapsed ? "" : "hidden sm:inline-flex"}`}
        onClick={(e) => { e.stopPropagation(); onToggle(); }}
      >
        {isCollapsed ? <ChevronDown className="size-3.5" /> : <ChevronUp className="size-3.5" />}
      </Button>
    </div>
  );
}

/* ─── Body ─── */

function DigestBody({ activeStyle, onAddItem, queuedIds }: { activeStyle: StyleKey; onAddItem?: (citation: MockCitation) => void; queuedIds?: Set<string> }) {
  const [hoveredKey, setHoveredKey] = useState<string | null>(null);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const points = SUMMARIES[activeStyle];

  /** Set hovered key immediately, cancelling any pending leave. */
  const handleEnter = (key: string) => {
    if (leaveTimer.current) { clearTimeout(leaveTimer.current); leaveTimer.current = null; }
    setHoveredKey(key);
  };

  /** Clear hovered key after a short delay so the mouse can cross the gap. */
  const handleLeave = () => {
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    leaveTimer.current = setTimeout(() => setHoveredKey(null), 80);
  };

  return (
    <div className="border-t border-border px-3 pb-3 pt-2 space-y-3 animate-in fade-in slide-in-from-top-1 duration-200">
      {/* Summary points */}
      <ul className="space-y-3">
        {points.map((point, pIdx) => (
          <li key={`${activeStyle}-${pIdx}`}>
            <p className="text-sm leading-relaxed">
              <Badge variant="outline" className="mr-1.5 align-middle text-[11px] px-1.5 py-0 h-[18px] text-primary/80 border-primary/30">
                {point.topic}
              </Badge>
              {point.segments.map((seg, sIdx) => {
                const key = `${activeStyle}-${pIdx}-${sIdx}`;
                if (seg.citationKey) {
                  return (
                    <CitationHighlight
                      key={key}
                      segmentKey={key}
                      text={seg.text}
                      citations={CITATIONS[seg.citationKey] ?? []}
                      isHovered={hoveredKey === key}
                      onEnter={handleEnter}
                      onLeave={handleLeave}
                      onAddItem={onAddItem}
                      queuedIds={queuedIds}
                    />
                  );
                }
                return <span key={key}>{seg.text}</span>;
              })}
            </p>
          </li>
        ))}
      </ul>

      {/* Footer */}
      <div className="flex items-center gap-3 text-xs text-muted-foreground pt-1 border-t border-border">
        <span className="flex items-center gap-1">
          <Clock className="size-3" />
          ~1h 45m total
        </span>
        <span>28 items analyzed</span>
      </div>
    </div>
  );
}

/* ─── Citation highlight with hover popup ─── */

function CitationHighlight({
  segmentKey,
  text,
  citations,
  isHovered,
  onEnter,
  onLeave,
  onAddItem,
  queuedIds,
}: {
  segmentKey: string;
  text: string;
  citations: MockCitation[];
  isHovered: boolean;
  onEnter: (key: string) => void;
  onLeave: () => void;
  onAddItem?: (citation: MockCitation) => void;
  queuedIds?: Set<string>;
}) {
  const anchorRef = useRef<HTMLSpanElement>(null);

  return (
    <span className="relative inline">
      <span
        ref={anchorRef}
        className="cursor-pointer rounded-sm bg-primary/25 px-0.5 transition-colors hover:bg-primary/35"
        onMouseEnter={() => onEnter(segmentKey)}
        onMouseLeave={onLeave}
      >
        {text}
      </span>
      {isHovered && citations.length > 0 && (
        <CitationPopup
          citations={citations}
          anchorRef={anchorRef}
          onMouseEnter={() => onEnter(segmentKey)}
          onMouseLeave={onLeave}
          onAddItem={onAddItem}
          queuedIds={queuedIds}
        />
      )}
    </span>
  );
}

/* ─── Citation popup (portal to body) ─── */

function CitationPopup({
  citations,
  anchorRef,
  onMouseEnter,
  onMouseLeave,
  onAddItem,
  queuedIds,
}: {
  citations: MockCitation[];
  anchorRef: React.RefObject<HTMLElement | null>;
  onMouseEnter: () => void;
  onMouseLeave: () => void;
  onAddItem?: (citation: MockCitation) => void;
  queuedIds?: Set<string>;
}) {
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useEffect(() => {
    const anchor = anchorRef.current;
    if (!anchor) return;
    const rect = anchor.getBoundingClientRect();
    let left = rect.left;
    const maxLeft = window.innerWidth - Math.min(400, window.innerWidth * 0.9) - 8;
    if (left > maxLeft) left = maxLeft;
    if (left < 8) left = 8;
    setPosition({ top: rect.bottom + 4, left });
  }, [anchorRef]);

  if (!position) return null;

  return createPortal(
    <div
      style={{ top: position.top, left: position.left }}
      className="fixed z-50 w-max max-w-[min(400px,90vw)] rounded-lg border border-border bg-popover p-2 shadow-lg"
      onMouseEnter={onMouseEnter}
      onMouseLeave={onMouseLeave}
    >
      <div className="flex flex-col gap-1.5 max-h-80 overflow-y-auto">
        {citations.map((c) => {
          const isQueued = queuedIds?.has(c.id) ?? false;
          return (
            <div
              key={c.id}
              className={`flex items-center gap-2.5 rounded-md p-1.5 transition-colors ${isQueued ? "opacity-60" : "hover:bg-muted"}`}
            >
              <div className="size-10 shrink-0 rounded bg-muted flex items-center justify-center text-xs text-muted-foreground">
                {c.type.charAt(0).toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <p className="text-sm font-medium leading-tight line-clamp-2">{c.title}</p>
                <div className="mt-0.5 flex items-center gap-1.5 text-xs text-muted-foreground">
                  <Badge variant="secondary" className="text-[10px] px-1 py-0 h-4">{c.type}</Badge>
                  <span className="truncate">{c.source}</span>
                  <span className="flex items-center gap-0.5 shrink-0">
                    <Clock className="size-3" />
                    {c.duration}
                  </span>
                </div>
              </div>
              {isQueued ? (
                <Badge variant="secondary" className="shrink-0 text-[10px] px-1.5 py-0 h-5">
                  In Queue
                </Badge>
              ) : (
                <Button
                  variant="default"
                  size="sm"
                  className="shrink-0 size-7 p-0"
                  onClick={() => onAddItem?.(c)}
                >
                  <Plus className="size-3.5" />
                </Button>
              )}
            </div>
          );
        })}
      </div>
    </div>,
    document.body,
  );
}

